/**
 * An S3 connection: the environment variables that point one instance at an
 * S3-compatible store (DDWS-S3-STORAGE-PLAN §2.2, §2.3, W1.6).
 *
 * The contract is the `DDWS_S3_*` set, written the same way whichever store
 * the tenant picks. The `AWS_*` copies are for the AWS SDKs and CLI, which
 * read them without any app-side configuration. An image that uses its own
 * names declares an `s3Env` mapping (a catalog entry or an SDL template), and
 * its variables are written from the same values.
 *
 * Nothing here is sent anywhere. The values go into the draft's environment,
 * which is where the host reads them: that is the same exposure as any other
 * environment variable, and the section says so.
 */

import type { GuidedEnvironment } from './guided'
import { parseImageRef } from './imageRef'

/**
 * The S3 instance images, by repository path. An instance running one of these
 * is offered as a source. The list is the image contract of W1.2: every image
 * serves S3 on container port 80 and reads the keys below.
 */
export const S3_INSTANCE_REPOSITORIES = ['ddws/s3-seaweedfs', 'ddws/s3-garage', 'ddws/s3-minio'] as const

/** The root keys every S3 instance image requires (W1.2). */
export const S3_ROOT_ACCESS_KEY = 'S3_ROOT_ACCESS_KEY'
export const S3_ROOT_SECRET_KEY = 'S3_ROOT_SECRET_KEY'
/** Per-app keys limited to named buckets: `ACCESS:SECRET:BUCKET[+BUCKET…]:rw|ro`, `;`-separated. */
export const S3_EXTRA_KEYS = 'S3_EXTRA_KEYS'

export const DEFAULT_S3_REGION = 'us-east-1'

/** The §2.2 contract, in the order it is written. */
export const S3_CONTRACT_KEYS = [
  'DDWS_S3_ENDPOINT',
  'DDWS_S3_REGION',
  'DDWS_S3_BUCKET',
  'DDWS_S3_ACCESS_KEY_ID',
  'DDWS_S3_SECRET_ACCESS_KEY',
  'DDWS_S3_FORCE_PATH_STYLE',
] as const

/** The copies for AWS SDKs and the CLI, written only while "Also set AWS_* variables" is ticked. */
export const S3_AWS_COPY_KEYS = ['AWS_ACCESS_KEY_ID', 'AWS_SECRET_ACCESS_KEY', 'AWS_REGION', 'AWS_ENDPOINT_URL_S3'] as const

/** The only placeholders an `s3Env` template may use (§2.3). */
export const S3_ENV_PLACEHOLDERS = ['endpoint', 'host', 'port', 'region', 'bucket', 'accessKeyId', 'secretAccessKey'] as const

export type S3EnvPlaceholder = (typeof S3_ENV_PLACEHOLDERS)[number]

/** An image's own variable names, each a template over the placeholders: `{ "OBJECTSTORE_S3_HOST": "{host}" }`. */
export type S3EnvMapping = Record<string, string>

const KEY_PATTERN = /^[A-Za-z_][A-Za-z0-9_]*$/
const RESERVED_KEYS = new Set<string>([...S3_CONTRACT_KEYS, ...S3_AWS_COPY_KEYS])

/**
 * Why a mapping is malformed, or null when it is sound. A catalog entry or a
 * template is checked with this when its module loads, so an unknown
 * placeholder fails the tests and the build instead of writing `{hots}` into
 * a tenant's environment.
 */
export function s3EnvMappingProblem(mapping: unknown): string | null {
  if (typeof mapping !== 'object' || mapping === null || Array.isArray(mapping)) return 'must be an object of KEY: template'
  const entries = Object.entries(mapping)
  if (entries.length === 0) return 'must name at least one variable'
  for (const [key, template] of entries) {
    if (!KEY_PATTERN.test(key)) return `${key} is not a valid variable name`
    if (RESERVED_KEYS.has(key)) return `${key} is written by every S3 connection and cannot be mapped`
    if (typeof template !== 'string') return `${key} must be a string template`
    const stripped = template.replace(/\{([^{}]*)\}/g, (_, name: string) => {
      if (!(S3_ENV_PLACEHOLDERS as readonly string[]).includes(name)) throw new UnknownPlaceholder(key, name)
      return ''
    })
    if (/[{}]/.test(stripped)) return `${key} has an unmatched brace`
  }
  return null
}

class UnknownPlaceholder extends Error {
  constructor(key: string, name: string) {
    super(`${key} uses unknown placeholder {${name}}; allowed: ${S3_ENV_PLACEHOLDERS.map((item) => `{${item}}`).join(', ')}`)
  }
}

/** The mapping, checked: throws with `where` in the message when it is malformed. */
export function checkS3EnvMapping(mapping: unknown, where: string): S3EnvMapping {
  let problem: string | null
  try {
    problem = s3EnvMappingProblem(mapping)
  } catch (err) {
    if (err instanceof UnknownPlaceholder) problem = err.message
    else throw err
  }
  if (problem) throw new Error(`${where}: s3Env ${problem}`)
  return mapping as S3EnvMapping
}

/** Whether an image reference names one of the S3 instance images, whatever its registry and tag. */
export function isS3InstanceImage(image: string): boolean {
  const ref = parseImageRef(image)
  if (!ref) return false
  const path = `${ref.registry}/${ref.repository}`.toLowerCase()
  return S3_INSTANCE_REPOSITORIES.some((repository) => path.endsWith(`/${repository}`))
}

export interface S3ConnectionValues {
  endpoint: string
  region: string
  bucket: string
  accessKeyId: string
  secretAccessKey: string
}

/**
 * Where a connection came from. Each source is resolved to the same
 * `S3ConnectionValues`, so a new source (Phase 2's managed buckets) adds a
 * member here and a panel in `S3Connection.tsx`, and nothing below changes.
 */
export type S3ConnectionSource = 'instance' | 'external'

/**
 * What the draft remembers about the connection it holds: which variables it
 * wrote, so editing or removing it touches exactly those and nothing the
 * tenant typed. The values themselves live only in the environment rows.
 */
export interface S3ConnectionRecord {
  source: S3ConnectionSource
  /** The S3 instance it points at, for the `instance` source. */
  dseq?: string | undefined
  awsCopies: boolean
  keys: string[]
}

/** A stored record, or undefined when it is not one (an older build, a hand edit). */
export function normalizeS3ConnectionRecord(value: unknown): S3ConnectionRecord | undefined {
  if (typeof value !== 'object' || value === null) return undefined
  const { source, dseq, awsCopies, keys } = value as Record<string, unknown>
  if (source !== 'instance' && source !== 'external') return undefined
  if (!Array.isArray(keys) || !keys.every((key) => typeof key === 'string' && KEY_PATTERN.test(key))) return undefined
  return {
    source,
    dseq: typeof dseq === 'string' && /^\d+$/.test(dseq) ? dseq : undefined,
    awsCopies: awsCopies !== false,
    keys: [...new Set(keys as string[])],
  }
}

const PRIVATE_REASON = 'Instances cannot reach private or local addresses. Use the address the store is reachable on from the internet.'

function ipv4Octets(host: string): number[] | null {
  const parts = host.split('.')
  if (parts.length !== 4 || !parts.every((part) => /^\d{1,3}$/.test(part))) return null
  const octets = parts.map(Number)
  return octets.every((octet) => octet <= 255) ? octets : null
}

/**
 * Whether an address literal is one a tenant instance cannot use. The
 * instance's network policy denies egress to RFC 1918 ranges (provider
 * `netpol.go`); loopback and link-local point at the instance itself or its
 * host, never at a store. Hostnames are not resolved here.
 */
export function isUnreachableAddress(hostname: string): boolean {
  const host = hostname.toLowerCase().replace(/^\[|\]$/g, '')
  if (host === 'localhost' || host.endsWith('.localhost')) return true
  const v4 = ipv4Octets(host) ?? ipv4Octets(host.replace(/^::ffff:/, ''))
  if (v4) {
    const [a = 0, b = 0] = v4
    return a === 0 || a === 10 || a === 127 || (a === 172 && b >= 16 && b <= 31) || (a === 192 && b === 168) || (a === 169 && b === 254)
  }
  if (!host.includes(':')) return false
  // IPv6: loopback, unspecified, unique-local fc00::/7, link-local fe80::/10.
  if (host === '::1' || host === '::') return true
  // The URL parser writes a mapped IPv4 address in hex: ::ffff:7f00:1.
  const mapped = /^::ffff:([0-9a-f]{1,4}):([0-9a-f]{1,4})$/.exec(host)
  if (mapped) {
    const high = parseInt(mapped[1]!, 16)
    const low = parseInt(mapped[2]!, 16)
    return isUnreachableAddress([high >> 8, high & 255, low >> 8, low & 255].join('.'))
  }
  const first = parseInt(host.split(':')[0] || '0', 16)
  return (first & 0xfe00) === 0xfc00 || (first & 0xffc0) === 0xfe80
}

/** The endpoint with its trailing slashes dropped, as the contract writes it. */
export function normalizeS3Endpoint(endpoint: string): string {
  return endpoint.trim().replace(/\/+$/, '')
}

/** Why an endpoint is refused, or null. */
export function s3EndpointProblem(endpoint: string): string | null {
  const value = normalizeS3Endpoint(endpoint)
  if (value === '') return 'Enter the endpoint.'
  if (!/^https:\/\//i.test(value)) return 'The endpoint must start with https://. Keys are only sent over an encrypted connection.'
  let url: URL
  try {
    url = new URL(value)
  } catch {
    return 'This is not a valid address.'
  }
  if (url.username || url.password) return 'Put the keys in their own fields, not in the address.'
  if (url.search || url.hash) return 'The endpoint cannot carry a query or a fragment.'
  // A path (often the bucket pasted with the host) is added to every request by
  // the SDKs, so path-style URLs come out wrong or fail their signature.
  if (url.pathname !== '/' && url.pathname !== '') return 'Enter only the address, such as https://s3.example.test. Put the bucket in its own field.'
  if (isUnreachableAddress(url.hostname)) return PRIVATE_REASON
  return null
}

/** S3 bucket naming rules: 3–63 characters of lower-case letters, digits, dots and hyphens. */
export function s3BucketProblem(bucket: string): string | null {
  const value = bucket.trim()
  if (value === '') return 'Enter the bucket name.'
  if (value.length < 3 || value.length > 63) return 'A bucket name is 3 to 63 characters long.'
  if (!/^[a-z0-9][a-z0-9.-]*[a-z0-9]$/.test(value)) return 'Use lower-case letters, digits, dots and hyphens, starting and ending with a letter or digit.'
  if (value.includes('..') || ipv4Octets(value)) return 'A bucket name cannot have two dots in a row or look like an IP address.'
  return null
}

export type S3ConnectionField = keyof S3ConnectionValues

/** Every field's problem; an empty object means the values can be written. */
export function s3ConnectionErrors(values: S3ConnectionValues): Partial<Record<S3ConnectionField, string>> {
  const errors: Partial<Record<S3ConnectionField, string>> = {}
  const endpoint = s3EndpointProblem(values.endpoint)
  if (endpoint) errors.endpoint = endpoint
  const bucket = s3BucketProblem(values.bucket)
  if (bucket) errors.bucket = bucket
  if (values.region.trim() !== '' && !/^[a-z0-9][a-z0-9-]{0,62}$/i.test(values.region.trim())) errors.region = 'A region is letters, digits and hyphens, for example us-east-1.'
  if (values.accessKeyId.trim() === '') errors.accessKeyId = 'Enter the access key.'
  else if (/\s/.test(values.accessKeyId.trim())) errors.accessKeyId = 'An access key has no spaces.'
  if (values.secretAccessKey === '') errors.secretAccessKey = 'Enter the secret key.'
  else if (/[\r\n]/.test(values.secretAccessKey)) errors.secretAccessKey = 'A secret key is a single line.'
  return errors
}

function placeholderValues(values: S3ConnectionValues): Record<S3EnvPlaceholder, string> {
  const endpoint = normalizeS3Endpoint(values.endpoint)
  let host = ''
  let port = '443'
  try {
    const url = new URL(endpoint)
    host = url.hostname
    port = url.port || '443'
  } catch { /* validated before this is reached */ }
  return {
    endpoint,
    host,
    port,
    region: values.region.trim() || DEFAULT_S3_REGION,
    bucket: values.bucket.trim(),
    accessKeyId: values.accessKeyId.trim(),
    secretAccessKey: values.secretAccessKey,
  }
}

/** One mapping template, filled in. */
export function renderS3EnvTemplate(template: string, values: S3ConnectionValues): string {
  const filled = placeholderValues(values)
  return template.replace(/\{([^{}]*)\}/g, (match, name: string) => (name in filled ? filled[name as S3EnvPlaceholder] : match))
}

/**
 * The variables a connection writes, in order: the contract, the AWS copies
 * when asked for, then the image's own names when it declares a mapping.
 */
export function s3ConnectionEnv(values: S3ConnectionValues, { awsCopies, mapping }: { awsCopies: boolean; mapping?: S3EnvMapping | undefined }): GuidedEnvironment[] {
  const filled = placeholderValues(values)
  const env: GuidedEnvironment[] = [
    { key: 'DDWS_S3_ENDPOINT', value: filled.endpoint },
    { key: 'DDWS_S3_REGION', value: filled.region },
    { key: 'DDWS_S3_BUCKET', value: filled.bucket },
    { key: 'DDWS_S3_ACCESS_KEY_ID', value: filled.accessKeyId },
    { key: 'DDWS_S3_SECRET_ACCESS_KEY', value: filled.secretAccessKey },
    { key: 'DDWS_S3_FORCE_PATH_STYLE', value: 'true' },
  ]
  if (awsCopies) {
    env.push(
      { key: 'AWS_ACCESS_KEY_ID', value: filled.accessKeyId },
      { key: 'AWS_SECRET_ACCESS_KEY', value: filled.secretAccessKey },
      { key: 'AWS_REGION', value: filled.region },
      { key: 'AWS_ENDPOINT_URL_S3', value: filled.endpoint },
    )
  }
  for (const [key, template] of Object.entries(mapping ?? {})) env.push({ key, value: renderS3EnvTemplate(template, values) })
  return env
}

/**
 * Whether "Also set AWS_* variables" starts ticked: yes, unless the service
 * already defines an `AWS_*` variable this connection did not write — the
 * tenant is configuring AWS access themselves, and a copy would overwrite it.
 */
export function defaultAwsCopies(env: GuidedEnvironment[], previous?: S3ConnectionRecord | undefined): boolean {
  if (previous) return previous.awsCopies
  return !env.some((row) => /^AWS_/.test(row.key.trim()))
}

/** Keys the connection would write that the environment already holds and it did not write. */
export function s3EnvConflicts(env: GuidedEnvironment[], next: GuidedEnvironment[], previous?: S3ConnectionRecord | undefined): string[] {
  const owned = new Set(previous?.keys ?? [])
  const present = new Set(env.map((row) => row.key.trim()).filter(Boolean))
  return next.map((row) => row.key).filter((key) => present.has(key) && !owned.has(key))
}

/**
 * Write a connection into the environment. The previous connection's
 * variables that the new one no longer writes are removed; a variable written
 * again keeps its row; a new one fills the first empty row, then appends.
 * Every other row is left exactly as it was.
 */
export function applyS3Connection(
  env: GuidedEnvironment[],
  previous: S3ConnectionRecord | undefined,
  next: GuidedEnvironment[],
  record: Omit<S3ConnectionRecord, 'keys'>,
): { env: GuidedEnvironment[]; record: S3ConnectionRecord } {
  const nextKeys = new Set(next.map((row) => row.key))
  const stale = new Set((previous?.keys ?? []).filter((key) => !nextKeys.has(key)))
  const out = env.filter((row) => !stale.has(row.key.trim()))
  for (const entry of next) {
    const existing = out.findIndex((row) => row.key.trim() === entry.key)
    if (existing >= 0) {
      out[existing] = { ...entry }
      continue
    }
    const empty = out.findIndex((row) => row.key.trim() === '' && row.value === '')
    if (empty >= 0) out[empty] = { ...entry }
    else out.push({ ...entry })
  }
  return { env: out, record: { ...record, keys: next.map((row) => row.key) } }
}

/** Remove exactly the variables the connection wrote. */
export function removeS3Connection(env: GuidedEnvironment[], record: S3ConnectionRecord): GuidedEnvironment[] {
  const owned = new Set(record.keys)
  return env.filter((row) => !owned.has(row.key.trim()))
}

/**
 * Re-write a saved connection for the image the draft holds now.
 *
 * The `s3Env` mapping is the image's, not the connection's: after the image
 * changes, the new image's own names are written and the old image's are
 * dropped, from the values the environment already holds. Null when the
 * connection's variables already match, so callers can leave the draft alone.
 */
export function reapplyS3Mapping(
  env: GuidedEnvironment[],
  record: S3ConnectionRecord,
  mapping: S3EnvMapping | undefined,
): { env: GuidedEnvironment[]; record: S3ConnectionRecord } | null {
  const next = s3ConnectionEnv(s3ValuesFromEnv(env), { awsCopies: record.awsCopies, mapping })
  const nextKeys = next.map((row) => row.key)
  if (nextKeys.length === record.keys.length && nextKeys.every((key) => record.keys.includes(key))) return null
  return applyS3Connection(env, record, next, { source: record.source, dseq: record.dseq, awsCopies: record.awsCopies })
}

/** The values the environment holds for a connection, for editing it. */
export function s3ValuesFromEnv(env: GuidedEnvironment[]): S3ConnectionValues {
  const value = (key: string) => env.find((row) => row.key.trim() === key)?.value ?? ''
  return {
    endpoint: value('DDWS_S3_ENDPOINT'),
    region: value('DDWS_S3_REGION'),
    bucket: value('DDWS_S3_BUCKET'),
    accessKeyId: value('DDWS_S3_ACCESS_KEY_ID'),
    secretAccessKey: value('DDWS_S3_SECRET_ACCESS_KEY'),
  }
}

export interface S3ExtraKey {
  accessKeyId: string
  secretAccessKey: string
  buckets: string[]
  mode: 'rw' | 'ro'
}

/** An S3 instance's per-app keys (W1.2 `S3_EXTRA_KEYS`). Malformed entries are skipped, never guessed at. */
export function parseS3ExtraKeys(value: string): S3ExtraKey[] {
  return value.split(';').flatMap((raw): S3ExtraKey[] => {
    const parts = raw.trim().split(':')
    if (parts.length !== 4) return []
    const [accessKeyId = '', secretAccessKey = '', bucketList = '', mode = ''] = parts
    const buckets = bucketList.split('+').filter(Boolean)
    if (!accessKeyId || !secretAccessKey || buckets.length === 0 || (mode !== 'rw' && mode !== 'ro')) return []
    return [{ accessKeyId, secretAccessKey, buckets, mode }]
  })
}

/** The keys an S3 instance was deployed with, as this browser signed them. */
export interface S3InstanceCredentials {
  root: { accessKeyId: string; secretAccessKey: string } | null
  extra: S3ExtraKey[]
}

/**
 * A running instance that serves S3, offered as a source. `credentials` is
 * null when this browser did not deploy it (no signed SDL here), and the
 * tenant types the keys.
 */
export interface S3InstanceCandidate {
  dseq: string
  name: string
  /** The service that runs the S3 image, whose address is the endpoint. */
  service: string
  credentials: S3InstanceCredentials | null
}

/** Read the S3 keys out of one service's environment rows. */
export function s3CredentialsFromEnv(env: GuidedEnvironment[]): S3InstanceCredentials {
  const value = (key: string) => env.find((row) => row.key.trim() === key)?.value ?? ''
  const accessKeyId = value(S3_ROOT_ACCESS_KEY)
  const secretAccessKey = value(S3_ROOT_SECRET_KEY)
  return {
    root: accessKeyId && secretAccessKey ? { accessKeyId, secretAccessKey } : null,
    extra: parseS3ExtraKeys(value(S3_EXTRA_KEYS)),
  }
}

/** The endpoint an instance's lease status reports for `service`: its ingress host, over HTTPS. */
export function s3EndpointFromUris(uris: readonly string[]): string | null {
  const uri = uris.find((item) => item.trim() !== '')
  if (!uri) return null
  const value = uri.trim().replace(/^[a-z]+:\/\//i, '')
  return normalizeS3Endpoint(`https://${value}`)
}
